import { get, post, put } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';
import { Appointment, CreateAppointmentDto } from '../../types/appointment';

export interface AppointmentQueryFilters {
  /** Exact appointment date (YYYY-MM-DD). */
  date?: string;
  /** Range start, inclusive (YYYY-MM-DD). */
  from?: string;
  /** Range end, inclusive (YYYY-MM-DD). */
  to?: string;
  doctorId?: string;
  departmentId?: string;
  status?: string;
}

export const appointmentService = {
  getAppointments: (
    page = 1,
    limit = 20,
    search?: string,
    filters?: AppointmentQueryFilters,
  ): Promise<ApiResponse<Appointment[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (filters?.date) params.set('date', filters.date);
    if (filters?.from) params.set('from', filters.from);
    if (filters?.to) params.set('to', filters.to);
    if (filters?.doctorId) params.set('doctorId', filters.doctorId);
    if (filters?.departmentId) params.set('departmentId', filters.departmentId);
    if (filters?.status) params.set('status', filters.status);
    return get<Appointment[]>(`/appointments?${params.toString()}`) as Promise<ApiResponse<Appointment[]> & { meta: PaginationMeta }>;
  },

  getAppointment: (id: string): Promise<ApiResponse<Appointment>> => {
    return get<Appointment>(`/appointments/${id}`);
  },

  createAppointment: (data: CreateAppointmentDto): Promise<ApiResponse<Appointment>> => {
    return post<Appointment>('/appointments', data);
  },

  updateAppointment: (id: string, data: Partial<CreateAppointmentDto>): Promise<ApiResponse<Appointment>> => {
    return put<Appointment>(`/appointments/${id}`, data);
  },

  cancelAppointment: (id: string): Promise<ApiResponse<Appointment>> => {
    return post<Appointment>(`/appointments/${id}/cancel`);
  },

  checkInAppointment: (id: string): Promise<ApiResponse<Appointment>> => {
    return post<Appointment>(`/appointments/${id}/checkin`);
  },

  bulkDeleteAppointments: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/appointments/bulk-delete', { ids });
  },

  resendConfirmation: (id: string): Promise<ApiResponse<{ sent: boolean }>> => {
    return post<{ sent: boolean }>(`/appointments/${id}/resend-confirmation`);
  },

  getDoctorSchedule: (doctorId: string): Promise<ApiResponse<any>> => {
    return get(`/appointments/schedule/${doctorId}`);
  },
};
